import {
  CALCULATION_CONTENT_CONTAINER_ID,
  VERSION_CONTENT_CONTAINER_ID,
} from '../../../shared/constants';
import { ContentArea } from '../types';
import {
  getNodeIgnoreFiberNodes,
  isFiberNode,
  getIgnoreNode,
  isTextNode,
} from './nodes';

const additionalCharacterLength = 10;

/**
 * Adjusts the offset. All dynamically added elements are ignored while still considering the length of their text contents.
 *
 * @param {Node} node - The current node.
 * @param {number} offset - The current position inside the node.
 * @returns {number} The position inside the node, in number of characters.
 */
const calculateOffset = (node: Node, offset: number): number => {
  const prevSibling = getNodeIgnoreFiberNodes(node, 'previousSibling');
  const parent = getNodeIgnoreFiberNodes(node, 'parentNode');

  if (parent && getIgnoreNode(parent)) {
    return calculateOffset(parent, offset);
  }

  if (
    !prevSibling ||
    (!getIgnoreNode(prevSibling) && !isTextNode(prevSibling))
  ) {
    return offset;
  }

  const nodeContentLength = prevSibling.textContent?.length || 0;
  const newOffset = offset + nodeContentLength;

  return calculateOffset(prevSibling, newOffset);
};

/**
 * Finds the number of siblings that are present before the given node in the DOM tree while ignoring all dynamically added elements.
 *
 * @param {Node} node - The node at some level of the DOM tree.
 * @returns {number} The number of siblings at the level of the input node.
 */
const findPreviousSiblings = (node: Node): number => {
  const previousSibling = getNodeIgnoreFiberNodes(node, 'previousSibling');

  if (previousSibling === null) {
    return 1;
  }

  if (
    (getIgnoreNode(node) || isTextNode(node) || isFiberNode(node)) &&
    (getIgnoreNode(previousSibling) || isTextNode(previousSibling))
  ) {
    return findPreviousSiblings(previousSibling);
  }

  return 1 + findPreviousSiblings(previousSibling);
};

/**
 * Calculates the `nodePath`.
 * Uses {@link findPreviousSiblings} to calculate the position of the node to access on each level of the DOM tree.
 *
 * @param {Node} node - The node from which the path is calculated.
 * @param {number[]} nodePath - Each number represents how many siblings a node has to the left; each index represents a level in the DOM.
 * @param {string} contentContainerId - The ID of the HTML element that contains the full HTML content from the database.
 * @param {Document} doc - The document the element is in (could be the actual document of the application or a calculation document).
 * @returns {number[]} Path to the start of the range.
 */
const findPath = (
  node: Node,
  nodePath: number[],
  contentContainerId: string,
  doc: Document,
): number[] => {
  if (isFiberNode(node) || getIgnoreNode(node)) {
    if (node.parentElement) {
      return findPath(node.parentElement, nodePath, contentContainerId, doc);
    }

    return nodePath;
  }

  const rootEl: Element | null | undefined =
    doc.getElementById(contentContainerId);
  const siblingsOnLevel = findPreviousSiblings(node);
  nodePath.push(siblingsOnLevel);

  if (node.parentElement !== rootEl && node.parentElement) {
    return findPath(node.parentElement, nodePath, contentContainerId, doc);
  }

  return nodePath;
};

/**
 * Calculates the first parent of the range's start node container with an ID that is also present in the database.
 *
 * @param {HTMLElement | null} element - The HTML element that is the start node of the range or a parent without an ID, or that is only calculated in the frontend.
 * @returns {string} The ID of the closest parent of the start node in the HTML from the database.
 */
export const getStartNodeContainerId = (
  element: HTMLElement | null,
): string => {
  // no parent with id found
  if (
    !element ||
    element.id === VERSION_CONTENT_CONTAINER_ID ||
    element.id === CALCULATION_CONTENT_CONTAINER_ID
  ) {
    return '';
  }
  // do not consider elements that are rendered in content
  if (getIgnoreNode(element as Node)) {
    return getStartNodeContainerId(element.parentElement);
  }
  if (element.id) {
    return element.id;
  }
  return getStartNodeContainerId(element.closest('[id]'));
};

/**
 * Finds the text before the pattern in the content.
 * Alters the range to start at the beginning of the document. The last `x` characters of the text content of the range, minus the length of the pattern, are returned.
 *
 * @param {Range} range - The range that contains the pattern of the mark.
 * @param {string} contentContainerId - The ID of the HTML element that contains the full HTML content from the database.
 * @param {Document} doc - The document the element is in (could be the actual document of the application or a calculation document).
 * @returns {string} The characters before the pattern.
 */
export const getPrecedingCharacters = (
  range: Range,
  contentContainerId: string,
  doc: Document,
): string => {
  const firstChild = doc.getElementById(contentContainerId)?.firstElementChild;
  if (!firstChild) {
    return '';
  }
  const patternLength = range.toString().length;

  range.setStartBefore(firstChild);

  const rangeString = range.toString();
  return rangeString.substring(
    rangeString.length - patternLength - additionalCharacterLength,
    rangeString.length - patternLength,
  );
};

/**
 * Finds the text after the pattern in the content.
 * Alters the range to end at the end of the document. The first `x` characters of the text content of the range, minus the length of the pattern, are returned.
 *
 * @param {Range} range - The range that contains the pattern of the mark.
 * @param {string} contentContainerId - The ID of the HTML element that contains the full HTML content from the database.
 * @param {Document} doc - The document the element is in (could be the actual document of the application or a calculation document).
 * @returns {string} The characters after the pattern.
 */
export const getSubsequentCharacters = (
  range: Range,
  contentContainerId: string,
  doc: Document,
): string => {
  const lastChild = doc.getElementById(contentContainerId)?.lastElementChild;
  if (!lastChild) {
    return '';
  }
  const patternLength = range.toString().length;

  range.setEndAfter(lastChild);

  const rangeString = range.toString();
  return rangeString.substring(
    patternLength,
    patternLength + additionalCharacterLength,
  );
};

/**
 * Converts the `Range` object to a `ContentArea` object that can be saved in the database.
 *
 * @param {Range} range - The range object delivered by the browser.
 * @param {string} contentContainerId - The ID of the HTML element (container) that is defined as the starting point of the path.
 * @param {Document} doc - The document that contains the HTML element.
 * @returns {ContentArea} The object that can be saved in the database.
 */
const convertFromRange = (
  range: Range,
  contentContainerId: string,
  doc: Document,
): ContentArea => {
  const startNodeContainerId = getStartNodeContainerId(
    range.startContainer.parentElement,
  );
  const precedingCharacters = getPrecedingCharacters(
    range.cloneRange(),
    contentContainerId,
    doc,
  );

  const subsequentCharacters = getSubsequentCharacters(
    range.cloneRange(),
    contentContainerId,
    doc,
  );
  const startOffset = calculateOffset(range.startContainer, range.startOffset);
  const endOffset = calculateOffset(range.endContainer, range.endOffset);
  const startPath: number[] = findPath(
    range.startContainer,
    [],
    contentContainerId,
    doc,
  );
  const endPath: number[] = findPath(
    range.endContainer,
    [],
    contentContainerId,
    doc,
  );

  return {
    startNodeContainerId,
    precedingCharacters,
    subsequentCharacters,
    patternString: range?.toString(),
    startNodeData: `{"nodePath": [${startPath}], "offset": ${startOffset}}`,
    endNodeData: `{"nodePath": [${endPath}], "offset": ${endOffset}}`,
  };
};

export default convertFromRange;
